import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Group,
  Loader,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { DroppableFileInput } from "@jasper/forms";

import {
  CreateOrderDesignDocument,
  GenerateOrderDesignUploadLinkDocument,
  GetInProgressOrderDesignDocument,
  OpenOrderDesignDocument,
} from "../../gql/generated/graphql";
import { useEffect, useState } from "react";
import { IconFile3d } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  orderId: string;
}

const UploadDesignModal = ({ isOpen, onClose, orderId }: Props) => {
  const [fetchInProgressOrderDesign, { loading: isFetchingLoading }] =
    useLazyQuery(GetInProgressOrderDesignDocument, { variables: { orderId } });
  const [createOrderDesign, { loading: isCreating }] = useMutation(
    CreateOrderDesignDocument
  );
  const [generateUploadLink, { loading: isGeneratingUploadLink }] = useMutation(
    GenerateOrderDesignUploadLinkDocument
  );
  const [openOrderDesign, { loading: isOpening }] = useMutation(
    OpenOrderDesignDocument
  );

  const [doesHaveOpenOrderDesign, setDoesHaveOpenOrderDesign] = useState(false);
  const [orderDesignId, setOrderDesignId] = useState<string>();
  const [fileList, setFileList] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    setDoesHaveOpenOrderDesign(false);
    setOrderDesignId(undefined);
    setFileList([]);
  }, [orderId]);

  // Init the order design
  const initialize = async () => {
    const { data: fetchData } = await fetchInProgressOrderDesign({
      variables: { orderId },
    });

    if (fetchData?.getInProgressOrderDesign?.status === "OPENED") {
      setDoesHaveOpenOrderDesign(true);
      return;
    }

    const draftOrderDesign = fetchData?.getInProgressOrderDesign;
    if (draftOrderDesign) {
      setOrderDesignId(draftOrderDesign.id);
    } else {
      const { data: createData } = await createOrderDesign({
        variables: { orderId },
      });

      if (createData?.createOrderDesign?.id) {
        setOrderDesignId(createData.createOrderDesign.id);
      }
    }
  };

  useEffect(() => {
    if (!isOpen || orderDesignId) {
      return;
    }

    void initialize();
  }, [
    isOpen,
    orderDesignId,
    fetchInProgressOrderDesign,
    createOrderDesign,
    orderId,
  ]);

  const handleSubmit = () => {
    if (!orderDesignId || !fileList.length) return;

    Promise.allSettled(
      fileList.map(file =>
        generateUploadLink({
          variables: { orderDesignId, filename: file.name },
        })
      )
    )
      .then(results => {
        setIsUploading(true);

        return Promise.allSettled(
          results.map(async (result, i) => {
            if (
              result.status === "rejected" ||
              !result.value?.data?.generateOrderDesignUploadLink
            ) {
              throw new Error(
                `Failed to generate upload link for ${fileList[i].name}`
              );
            }

            try {
              const { url, fields } =
                result.value.data.generateOrderDesignUploadLink;
              const formData = new FormData();
              Object.entries(fields).forEach(([key, value]) => {
                formData.append(key, value as string);
              });

              formData.append("file", fileList[i]);

              const res = await fetch(url, {
                method: "POST",
                body: formData,
              });

              if (!res.ok) {
                throw new Error(
                  `Failed to generate upload link for ${fileList[i].name}`
                );
              }
            } catch (err) {
              notifications.show({
                title: "Upload failed",
                color: "red",
                message: `Failed to upload ${fileList[i].name}`,
              });

              throw err;
            }
          })
        );
      })
      .then(results => {
        const failedResults = results.filter(
          ({ status }) => status === "rejected"
        );
        if (failedResults.length) {
          const allFailed = failedResults.length === results.length;
          notifications.show({
            title: allFailed
              ? "Failed to upload files"
              : "Some files couldn't be uploaded",
            color: allFailed ? "red" : "orange",
            message: allFailed
              ? `Couldn't upload any files, please retry`
              : `${failedResults.length} failed to upload, please review the design`,
          });

          if (allFailed) throw new Error("Failed to upload any files");
        } else {
          notifications.show({
            title: "Files uploaded",
            color: "green",
            message: "All files uploaded successfully",
          });
        }

        return openOrderDesign({ variables: { orderDesignId } });
      })
      .then(() => {
        setDoesHaveOpenOrderDesign(true);
      })
      .finally(() => {
        setIsUploading(false);
        onClose();
      });
  };

  return (
    <Modal opened={isOpen} onClose={onClose}>
      <Box pos="relative">
        <LoadingOverlay
          visible={
            isFetchingLoading ||
            isCreating ||
            (!orderDesignId && !doesHaveOpenOrderDesign) ||
            isGeneratingUploadLink ||
            isUploading ||
            isOpening
          }
          loaderProps={{ children: <Loader /> }}
        />

        {doesHaveOpenOrderDesign ? (
          <Text>An order design is already open for this order.</Text>
        ) : (
          <Stack align="center" gap="md">
            <Title>Upload your design</Title>

            <DroppableFileInput
              acceptedTypes={[".stl"]}
              onChange={setFileList}
              files={fileList}
              fileIcon={<IconFile3d />}
              title="Drag and drop 3D files to upload"
              description={`Your files won't be sent until you click "Validate"`}
            />

            <Group w="100%" justify="space-between">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="filled"
                disabled={
                  fileList.length === 0 || isFetchingLoading || isCreating
                }
                onClick={handleSubmit}
              >
                Validate
              </Button>
            </Group>
          </Stack>
        )}
      </Box>
    </Modal>
  );
};

export default UploadDesignModal;
